/**
 * ResidentPublicInfoBlock (#450 — the #420b resident half, migration 0133).
 *
 * The operator-facing RESIDENT PUBLIC-INFORMATION surface, mounted inside the incident work
 * surface as a PARALLEL SIBLING to the Public Information Draft + Public Alert Request blocks.
 * A municipal Emergency Information Officer (EIO) COMPOSES a plain-language release and PUBLISHES
 * it to the municipality's OWN opted-in resident list over its OWN channel — Ontario IMS §7.10 /
 * Appendix A #62 ("the public information management section … develops and shares messages with
 * the public directly"); the EIO is App A #23. The municipality ISSUES its OWN release.
 *
 * IT IS NOT ALERT READY. The national wireless broadcast is operated by the PEOC "on behalf of
 * communities" (PERP §5.5.1) and a municipality only REQUESTS it via the Public Alert Request
 * block (request_public_alert → PEOC, #497). The register-guard line names that escalation door.
 * This surface never routes through, imitates, or is labelled as Alert Ready.
 *
 * RESIDENT-BODY DISCIPLINE (mirrors PublicAlertRequestBlock's foreign-value guard): nothing
 * operator-facing reaches a resident. The pre-fill from the Public Information Draft has its [n]
 * evidence markers + chrome STRIPPED (toResidentReleaseBody); any hazard reference renders ONLY
 * through the doctrine-safe HAZARD_LABEL map (never the raw enum). The operator is the author of
 * record — nothing is sent until Publish.
 */
import { useState, type JSX } from 'react';
import {
  PUBLIC_INFO_BODY_MAX_LEN,
  toResidentReleaseBody,
  type InZoneCommunity,
} from '@tsr/shared-types';
import {
  PUBLIC_INFO_RELEASE_HEADING,
  PUBLISH_PUBLIC_INFO_LABEL,
  PUBLISH_PUBLIC_INFO_BUSY_LABEL,
  EIO_AUTHOR_OF_RECORD_LABEL,
  PUBLIC_INFO_COMPOSE_HELP,
  PREFILL_FROM_PUBLIC_INFO_DRAFT_LABEL,
  NO_TARGET_COMMUNITY_EMPTY,
  residentReleaseRegisterGuard,
  publishedToResidentsToast,
  noResidentsSubscribedToast,
} from '../incidentVocabulary';
import { draftPublicInfoForResident, publishPublicInfo, fetchCommunityPickList } from '../api';
import { getSessionId } from '../lib/operatorActions';
import { showToast } from '../lib/toast';

const inputCls =
  'w-full rounded border border-slate-700 bg-slate-900/70 px-2 py-1 text-[12px] text-slate-100 focus:border-teal-600 focus:outline-none';

/**
 * Doctrine-safe hazard labels (the COP's OWN enum → a human label). FOREIGN-VALUE GUARD: a
 * hazard value NOT in this map falls back to the neutral 'Hazard' — NEVER the raw value, so a
 * raw CAP/status string can never leak onto the surface. Mirrors PublicAlertRequestBlock.
 */
const HAZARD_LABEL: Record<string, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  ice_storm: 'Ice Storm',
  cap_warning: 'Public Alert',
  multi_hazard: 'Multi-Hazard',
  other: 'Incident',
};
function hazardLabel(hazardType: string | null | undefined): string | null {
  if (!hazardType) return null;
  return HAZARD_LABEL[hazardType] ?? 'Hazard';
}

export function ResidentPublicInfoBlock({
  incidentId,
  hazardType,
  inZoneCommunities,
}: {
  incidentId: string;
  /** The COP's OWN hazard enum — rendered ONLY through HAZARD_LABEL (never raw). */
  hazardType?: string | null;
  /** Communities covered by the incident's protective-action zone — the targetable residents. */
  inZoneCommunities: InZoneCommunity[];
}): JSX.Element {
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [body, setBody] = useState('');
  const [targetId, setTargetId] = useState<string>(inZoneCommunities[0]?.id ?? '');
  // Pick-list fallback: a public-information release is NOT gated on a protective-action zone,
  // so when the incident has no in-zone community the EIO picks any community (fetched on open).
  const [pickList, setPickList] = useState<{ id: string; name: string }[] | null>(null);
  const [loadingPick, setLoadingPick] = useState(false);

  const usingInZone = inZoneCommunities.length > 0;
  const options: { id: string; name: string }[] = usingInZone
    ? inZoneCommunities.map((c) => ({ id: c.id, name: c.name }))
    : (pickList ?? []);
  const targetCommunity =
    options.find((c) => c.id === targetId) ?? (usingInZone ? (options[0] ?? null) : null);
  const hazard = hazardLabel(hazardType);

  const openForm = (): void => {
    setFormOpen(true);
    // Lazily fetch the community roster only when there's no in-zone target (user-action
    // triggered, never on mount — keeps the shared-component partial-mock case safe).
    if (!usingInZone && pickList === null && !loadingPick) {
      setLoadingPick(true);
      void fetchCommunityPickList()
        .then((list) => setPickList(list))
        .catch(() => setPickList([]))
        .finally(() => setLoadingPick(false));
    }
  };

  const doPrefill = (): void => {
    if (prefilling) return;
    setPrefilling(true);
    void draftPublicInfoForResident(incidentId, getSessionId())
      .then((res) => {
        if (res.status === 'drafted' && res.draft) {
          setBody(toResidentReleaseBody(res.draft));
          showToast('Pre-filled from the Public Information Draft — review before publishing.', {
            variant: 'success',
          });
        } else {
          showToast('No draft available yet — write the release manually.', { variant: 'info' });
        }
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not pre-fill from the draft.', { variant: 'error' }),
      )
      .finally(() => setPrefilling(false));
  };

  const doPublish = (): void => {
    if (busy || !targetCommunity || body.trim().length === 0) return;
    setBusy(true);
    // Strip [n] markers client-side too (defensive — the server also strips).
    const residentBody = toResidentReleaseBody(body);
    void publishPublicInfo({
      community_id: targetCommunity.id,
      incident_id: incidentId,
      body: residentBody,
      session_id: getSessionId(),
    })
      .then((res) => {
        setFormOpen(false);
        setBody('');
        showToast(
          res.recipient_count === 0
            ? noResidentsSubscribedToast(res.community_name)
            : publishedToResidentsToast(res.community_name, res.delivered_count),
          { variant: 'success' },
        );
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not publish the release.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  return (
    <section data-testid="resident-public-info-block" className="space-y-1.5">
      <h4 className="text-[10px] uppercase tracking-wider text-slate-400">
        {PUBLIC_INFO_RELEASE_HEADING}
      </h4>
      <p className="text-[10px] text-slate-500">{PUBLIC_INFO_COMPOSE_HELP}</p>

      {!formOpen ? (
        <button
          type="button"
          data-testid="publish-public-info-open"
          onClick={openForm}
          className="w-full rounded border border-teal-500/60 py-1.5 text-[11px] text-teal-200 hover:bg-teal-600/20"
        >
          {PUBLISH_PUBLIC_INFO_LABEL}
        </button>
      ) : (
        <div className="space-y-1.5 rounded border border-slate-700 bg-slate-900/50 p-2">
          {/* Target community (residents are bound to a community). In-zone communities are the
              default; when the incident has none, the EIO picks any community from the roster. */}
          {usingInZone ? (
            inZoneCommunities.length === 1 ? (
              <p className="text-[11px] text-slate-300" data-testid="resident-release-community">
                Publishing to{' '}
                <span className="font-medium text-teal-200">{targetCommunity?.name}</span> residents
                {hazard ? ` · ${hazard}` : ''}
              </p>
            ) : (
              <label className="block text-[10px] text-slate-400">
                Community
                <select
                  data-testid="resident-release-community"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className={inputCls}
                >
                  {inZoneCommunities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )
          ) : loadingPick ? (
            <p className="text-[11px] text-slate-500" data-testid="resident-release-loading">
              Loading communities…
            </p>
          ) : options.length > 0 ? (
            <label className="block text-[10px] text-slate-400">
              Community
              <select
                data-testid="resident-release-community"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className={inputCls}
              >
                <option value="">Select a community…</option>
                {options.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p data-testid="resident-release-no-target" className="text-[11px] text-amber-300/90">
              {NO_TARGET_COMMUNITY_EMPTY}
            </p>
          )}

          {(usingInZone || loadingPick || options.length > 0) && (
            <>
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-slate-400">Resident release</label>
                <button
                  type="button"
                  data-testid="resident-release-prefill"
                  onClick={doPrefill}
                  disabled={prefilling}
                  className="rounded border border-slate-600 px-2 py-0.5 text-[10px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  {prefilling ? 'Pre-filling…' : PREFILL_FROM_PUBLIC_INFO_DRAFT_LABEL}
                </button>
              </div>
              <textarea
                data-testid="resident-release-body"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, PUBLIC_INFO_BODY_MAX_LEN))}
                rows={4}
                maxLength={PUBLIC_INFO_BODY_MAX_LEN}
                placeholder="e.g. Boil-water advisory in effect. Use bottled water until further notice."
                className={inputCls}
              />
              <p className="text-right text-[9px] text-slate-500">
                {body.length}/{PUBLIC_INFO_BODY_MAX_LEN}
              </p>

              {/* Register guard (GAP-450-B): channel + reach + the escalation door. */}
              <p
                data-testid="resident-release-register-guard"
                className="rounded border border-slate-700/70 bg-slate-800/40 p-1.5 text-[10px] leading-snug text-slate-400"
              >
                {targetCommunity ? residentReleaseRegisterGuard(targetCommunity.name) : ''}
              </p>

              {/* Author of record (App A #23 — EIO, never PIO). */}
              <p data-testid="resident-release-eio-byline" className="text-[10px] text-slate-500">
                {EIO_AUTHOR_OF_RECORD_LABEL}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="publish-public-info-submit"
                  onClick={doPublish}
                  disabled={busy || !targetCommunity || body.trim().length === 0}
                  className="flex-1 rounded bg-teal-600 py-1.5 text-[11px] font-medium text-white hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? PUBLISH_PUBLIC_INFO_BUSY_LABEL : PUBLISH_PUBLIC_INFO_LABEL}
                </button>
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  className="rounded border border-slate-700 px-3 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
